import { useTheme } from '@material-ui/core'
import { isEqual } from 'lodash'
import { MouseEvent } from 'react'
import { useAppSelector } from 'store/configureStore'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  XAxisProps,
  YAxis,
} from 'recharts'
import {
  MoneyDto,
  PriceSummaryDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { RootAppState } from 'store/configureStore'
import { MoneyAxisTick } from '../../../Graph/YAxisTick'

const MaterialXAxisTick = (
  props: {
    onMouseEnter: (e: MouseEvent, index: number) => void
    onMouseLeave: (e: MouseEvent) => void
    onClick: (index: number) => void
    payload: { index: number; value: string }
  } & XAxisProps
) => {
  const theme = useTheme()
  const { x, y, payload } = props
  return (
    <g
      transform={`translate(${x},${y})`}
      onClick={() => {
        props.onClick(payload.index)
      }}
      onMouseEnter={(e) => {
        props.onMouseEnter(e, payload.index)
      }}
      onMouseLeave={(e) => props.onMouseLeave(e)}
      style={{ cursor: 'pointer' }}
    >
      <text
        x={0}
        y={payload.index % 2 === 0 ? 4 : 24}
        textAnchor="middle"
        fill={theme.palette.text.primary}
        // transform="rotate(-35)"
      >
        {payload.value}
      </text>
    </g>
  )
}

type GraphData = {
  materialId: string
  totalSalesPrice: MoneyDto
}

export const MaterialsBarChart = (props: {
  priceSummaryKeys: Array<string>
  handleMouseEnter: (e: MouseEvent, priceSummary: PriceSummaryDto) => void
  scrollAndFilter: (priceSummary: PriceSummaryDto) => void
  containerHeight?: number
}) => {
  const theme = useTheme()

  const priceSummariesSum: Array<GraphData> = useAppSelector(
    (state: RootAppState) => {
      const graphData = props.priceSummaryKeys.reduce((acc, key) => {
        const priceSummary = state.project.priceSummaries[key]

        if (!priceSummary.isMaterial) {
          return acc
        }

        acc.push({
          materialId: priceSummary.materialId,
          description: priceSummary.description,
          totalSalesPrice: priceSummary.totalSalesPrice,
          mainActivityCostPrice: priceSummary.totalCostPrice,
          surchargeValue: priceSummary.surchargeValue,
          discountValue: priceSummary.discountValue,
          totalQuantity: priceSummary.totalQuantity,
          staggeredTotalQuantity: priceSummary.staggeredTotalQuantity,
          isMaterial: priceSummary.isMaterial,
          isPurchasePartWithoutArticle:
            priceSummary.isPurchasePartWithoutArticle,
        })

        return acc
      }, [])

      graphData.length &&
        graphData.sort(
          (a, b) => b.totalSalesPrice?.value - a.totalSalesPrice?.value
        )

      return graphData
    },
    isEqual
  )

  const priceSummaries = useAppSelector((state: RootAppState) => {
    return props.priceSummaryKeys
      .map((x) => {
        // const p = { ...state.project.priceSummaries[x] }
        // p.discountValue = {
        //   ...p.discountValue,
        //   value: p.discountValue.value * -1,
        // }
        return {
          ...state.project.priceSummaries[x],
          category: state.project.priceSummaries[x].isMaterial
            ? 'material'
            : 'workingSteps',
        }
      })
      .sort((a, b) => b.totalSalesPrice?.value - a.totalSalesPrice?.value)
  }, isEqual)

  return (
    <ResponsiveContainer width={'100%'} height={props.containerHeight || 204}>
      <BarChart data={priceSummariesSum} margin={{ bottom: 0 }}>
        {/* <Tooltip /> */}
        <CartesianGrid strokeDasharray={'3 3'} />
        <XAxis
          dataKey="materialId"
          type="category"
          interval={0}
          tickMargin={12}
          height={42}
          tick={(_props) => (
            <MaterialXAxisTick
              {..._props}
              onMouseEnter={(e, index) =>
                props.handleMouseEnter(e, priceSummariesSum[index])
              }
              onMouseLeave={(e) => props.handleMouseEnter(e, undefined)}
              onClick={(index) =>
                props.scrollAndFilter(priceSummariesSum[index])
              }
            />
          )}
        />
        <YAxis
          type="number"
          // ticks={priceSummaries
          //   .map((x) => x.totalSalesPrice.value)
          //   .sort()
          //   .concat(projectNetSalesPrice.value)}
          tick={
            <MoneyAxisTick defaultMoney={priceSummaries[0].totalSalesPrice} />
          }
          minTickGap={10}
          interval={'preserveStartEnd'}
          allowDataOverflow={false}
          domain={[0, 'dataMax']}
        />
        <Bar
          dataKey={'totalSalesPrice.value'}
          fill={theme.palette.info.dark}
          onMouseEnter={(data, _index, e) => props.handleMouseEnter(e, data)}
          onMouseLeave={(data, _index, e) =>
            props.handleMouseEnter(e, undefined)
          }
          barSize={32}
          onClick={(data) => props.scrollAndFilter(data)}
          style={{
            cursor: 'pointer',
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}
